"use client";

import type React from "react";
import CheckIcon from "@/assets/images/check.svg";
import { DEFAULT_OTP_LENGTH, RESEND_OTP_INTERVAL_SECONDS } from "@/config";
import CButton from "@/Shared/CButton/CButton";
import OtpInput from "@/Shared/Form/OtpInput";
import InfoBanner from "./InfoBanner";
import PasswordField from "./PasswordField";

interface Props {
  email: string;
  otp: string;
  newPassword: string;
  confirmPassword: string;
  setOtp: (v: string) => void;
  setNewPassword: (v: string) => void;
  setConfirmPassword: (v: string) => void;
  onVerify: () => void;
  onOtpComplete?: (v: string) => void;
  onResend?: () => void;
  secondsRemaining?: number;
  loading?: boolean;
  infoMessage?: string | null;
  onDismissInfo?: () => void;
  onChangeEmail?: () => void;
  otpVerified?: boolean;
  otpLength?: number;
}

/**
 * VerifyStep — OTP input + new password fields + reset button.
 */
const VerifyStep: React.FC<Props> = ({
  email,
  otp,
  newPassword,
  confirmPassword,
  setOtp,
  setNewPassword,
  setConfirmPassword,
  onVerify,
  onOtpComplete,
  onResend,
  secondsRemaining = 0,
  loading = false,
  infoMessage,
  onDismissInfo,
  onChangeEmail,
  otpVerified = false,
  otpLength = DEFAULT_OTP_LENGTH,
}) => {
  return (
    <>
      <InfoBanner
        message={infoMessage ?? null}
        onDismiss={() => onDismissInfo?.()}
        onResend={onResend}
        secondsRemaining={secondsRemaining}
        totalSeconds={RESEND_OTP_INTERVAL_SECONDS}
        loading={loading}
      />

      <p className="mb-4 text-sm text-muted-foreground">
        We sent an OTP to <strong>{email}</strong>. Enter it below with your new
        password.
      </p>

      <div className="mb-4 flex flex-col items-center gap-3">
        <OtpInput
          length={otpLength}
          value={otp}
          onChange={(v) => setOtp(v)}
          onComplete={(v) => onOtpComplete?.(v)}
          autoFocus
          aria-label="otp-group"
        />
        {otpVerified && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-success-50 py-1 pl-1.5 pr-3 text-xs font-medium text-success-700">
            <span className="flex size-4 items-center justify-center rounded-full bg-success-600 text-white">
              <CheckIcon className="size-3" />
            </span>
            OTP verified
          </span>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          onVerify();
        }}
        className="space-y-4"
      >
        <PasswordField
          id="reset-new-password"
          label="New Password"
          value={newPassword}
          onChange={setNewPassword}
          placeholder="Enter new password"
          required
          minLength={8}
          disabled={loading}
        />

        <PasswordField
          id="reset-confirm-password"
          label="Confirm Password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder="Confirm new password"
          required
          minLength={8}
          disabled={loading}
        />

        <CButton
          type="submit"
          size="lg"
          className="w-full"
          disabled={loading || !otp || !newPassword || !confirmPassword}
        >
          {loading ? "Resetting..." : "Reset Password"}
        </CButton>
      </form>

      <div className="mt-2 text-sm text-muted-foreground flex gap-2">
        <CButton variant="link" size="sm" onClick={onChangeEmail}>
          Change email
        </CButton>
      </div>
    </>
  );
};

export default VerifyStep;
